import Button from '../atoms/button'
import {
  FiChevronLeft as LeftIcon,
  FiChevronRight as RightIcon,
  FiChevronsLeft as FirstIcon,
  FiChevronsRight as LastIcon,
} from 'react-icons/fi'
import { useEffect, useState } from 'react'

const Pagination = ({ total, limit, page, setPage, size = 'md' }) => {
  const numPages = Math.ceil(total / limit) || 1

  // 페이지네이션에 표시할 페이지 번호 생성
  const getPageNumbers = () => {
    const delta = 2 // 현재 페이지 좌우로 보여줄 페이지 수
    const range = []
    const rangeWithDots = []
    let l

    for (let i = 1; i <= numPages; i++) {
      if (
        i === 1 ||
        i === numPages ||
        (i >= page - delta && i <= page + delta)
      ) {
        range.push(i)
      }
    }

    range.forEach((i) => {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1)
        } else if (i - l !== 1) {
          rangeWithDots.push('...')
        }
      }
      rangeWithDots.push(i)
      l = i
    })

    return rangeWithDots
  }

  // 현재 표시 중인 아이템 범위 계산
  const startItem = (page - 1) * limit + 1
  const endItem = Math.min(page * limit, total)

  const styleBySize = {
    md: 'mx-1 text-md font-normal',
    lg: 'mx-1 text-lg font-normal',
  }

  return (
    <div className='flex flex-col items-center gap-3'>
      {/* 페이지 정보 */}
      <div className='text-sm text-gray-600'>
        <span className='font-medium'>{startItem}</span>
        {' - '}
        <span className='font-medium'>{endItem}</span>
        {' / '}
        <span className='font-medium'>{total}</span>개 표시 중
      </div>

      {/* 페이지네이션 버튼들 */}
      <nav className='flex items-center gap-1'>
        {/* 첫 페이지로 이동 */}
        <button
          type='button'
          onClick={() => setPage(1)}
          disabled={page === 1}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${
              page === 1
                ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm'
            }
          `}
          aria-label='첫 페이지'
        >
          <FirstIcon className='w-4 h-4' />
        </button>

        {/* 이전 페이지 */}
        <button
          type='button'
          onClick={() => setPage(Math.max(1, page - 1))}
          disabled={page === 1}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${
              page === 1
                ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm'
            }
          `}
          aria-label='이전 페이지'
        >
          <LeftIcon className='w-4 h-4' />
        </button>

        {/* 페이지 번호들 */}
        <div className='flex items-center gap-1 px-2'>
          {getPageNumbers().map((pageNum, idx) =>
            pageNum === '...' ? (
              <span key={`dots-${idx}`} className='px-3 py-2 text-gray-400'>
                ···
              </span>
            ) : (
              <button
                key={pageNum}
                type='button'
                onClick={() => setPage(pageNum)}
                className={`
                  min-w-[40px] px-3 py-2 rounded-lg text-sm font-medium
                  transition-all duration-200 transform
                  ${
                    page === pageNum
                      ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md scale-105'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm hover:scale-105'
                  }
                `}
              >
                {pageNum}
              </button>
            ),
          )}
        </div>

        {/* 다음 페이지 */}
        <button
          type='button'
          onClick={() => setPage(Math.min(numPages, page + 1))}
          disabled={page === numPages}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${
              page === numPages
                ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm'
            }
          `}
          aria-label='다음 페이지'
        >
          <RightIcon className='w-4 h-4' />
        </button>

        {/* 마지막 페이지로 이동 */}
        <button
          type='button'
          onClick={() => setPage(numPages)}
          disabled={page === numPages}
          className={`
            px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
            ${
              page === numPages
                ? 'text-gray-400 cursor-not-allowed bg-gray-50'
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:shadow-sm'
            }
          `}
          aria-label='마지막 페이지'
        >
          <LastIcon className='w-4 h-4' />
        </button>
      </nav>

      {/* 페이지 점프 (선택사항) */}
      <div className='flex items-center gap-2 text-sm'>
        <span className='text-gray-600'>페이지로 이동:</span>
        <input
          type='number'
          min='1'
          max={numPages}
          value={page}
          onChange={(e) => {
            const value = parseInt(e.target.value, 10)
            if (value >= 1 && value <= numPages) {
              setPage(value)
            }
          }}
          className='w-16 px-2 py-1 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
        />
        <span className='text-gray-600'>/ {numPages}</span>
      </div>
    </div>
  )
}

export default Pagination
